/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import {
  Sizes,
  Positions,
  Icons,
  TextInput,
  Card,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector } from 'react-redux';
import { ActionMenu, NGContainer } from '../NominationGroups/style';
import Table from 'src/UI/Table';
import Button from 'src/UI/Button';
import { searchByText } from 'src/utils/common/utils';
import { NUMBER_20 } from 'src/utils/table';
import { get } from 'src/services/GenesisApi';
import { DOWNLOAD_LOCATIONS, GET_LOCATIONS, MESSAGES } from 'src/constants/EUMarkets';
import DownloadButton from 'src/UI/Download';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { sendNotification } from 'src/UI/Notification';
import { ILocationItem } from 'src/types/EUMarkets.type';
import { IFullState } from 'src/types/All.type';
import { rolePermissions } from 'src/constants/Permission';
import { getEnv } from 'src/utils/env.vite';
import Parameters from '../Parameters';
import AddEditLocation from './AddEdit';

const { ArrowCcw, Search, Add: AddIcon, EllipsisVertical } = Icons;

// Add/Modify are only available in the Dev environment until the save APIs are ready elsewhere.
const isDevEnvironment = () => getEnv('VITE_APP_ENVIRONMENT') === 'Dev';

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<ILocationItem[]>([]);
  const [openParams, setOpenParams] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [location, setLocation] = useState<ILocationItem | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getData = () => {
    // API call to fetch data
    setLoading(true);
    get(GET_LOCATIONS)
      .then((response) => {
        setLocations(response.data || []);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleParamsOpenClose = () => {
    if (openParams) {
      setLocation(null);
    }
    setOpenParams(!openParams);
  };

  const handleModalOpenClose = () => {
    if (open) {
      setLocation(null);
    }
    setOpen(!open);
  };

  const handleEdit = (item: ILocationItem) => {
    setLocation(item);
    setOpen(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, data: ILocationItem[]) => {
    return searchByText(data, text, ['locationName', 'locationType', 'organizationName']);
  };

  const handleShowParams = (item: ILocationItem) => {
    setLocation(item);
    setOpenParams(true);
  };

  const renderActions = (item: ILocationItem) => (
    <ActionMenu
      action='click'
      popup={
        <div className='menu-container'>
          {isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData) && (
            <Button
              data-testid={`btn-modify-${item.id}`}
              size='small'
              onClick={() => handleEdit(item)}
              variant='transparent'
            >
              Modify
            </Button>
          )}
        </div>
      }
    >
      <Button
        size='small'
        data-testid={`btn-actions-${item.id}`}
        variant='transparent'
        onClick={() => {}}
        icon={<EllipsisVertical />}
        iconOnly
      />
    </ActionMenu>
  );

  const showActions = isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData);

  const columns = [
    {
      accessKey: 'locationName',
      fieldName: 'locationName',
      title: 'Location Name',
      sorting: true,
    },
    {
      accessKey: 'locationType',
      fieldName: 'locationType',
      title: 'Location Type',
      sorting: true,
    },
    {
      accessKey: 'organizationName',
      fieldName: 'organizationName',
      title: 'Organization Name',
      sorting: true,
    },
    {
      accessKey: 'validFrom',
      fieldName: 'validFrom',
      title: 'Valid From',
      sorting: true,
      render: (item: ILocationItem) =>
        item.validFrom ? dayjs(item.validFrom).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : '-',
    },
    {
      accessKey: 'validTo',
      fieldName: 'validTo',
      title: 'Valid To',
      sorting: true,
      render: (item: ILocationItem) =>
        item.validTo ? dayjs(item.validTo).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry',
    },
    {
      accessKey: 'parameters',
      fieldName: 'parameters',
      title: 'Parameters',
      render: (item) => (
        <Button
          data-testid={`btn-params-${item.id}`}
          size='small'
          onClick={() => handleShowParams(item)}
          key={`tsk-add-btn-${item.id}`}
          variant='transparent'
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    ...(showActions
      ? [
          {
            accessKey: 'actions',
            fieldName: 'actions',
            title: 'Actions',
            render: (item: ILocationItem) => renderActions(item),
          },
        ]
      : []),
  ];

  return (
    <NGContainer>
      <div className='action-container'>
        <div className='search-container'>
          <TextInput
            placeholder='Search'
            onChange={handleSearch}
            suffix={{
              node: <Search height={24} width={24} />,
            }}
            size={Sizes.Small}
          />
        </div>
        {isDevEnvironment() && permissions.includes(rolePermissions.createMasterData) && (
          <Button
            onClick={handleModalOpenClose}
            data-testid='add-new-location'
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
          >
            Add
          </Button>
        )}
        <DownloadButton
          downloadURL={DOWNLOAD_LOCATIONS}
          method='GET'
          fileName='Locations'
          fileType='xlsx'
          buttonProp={{
            size: Sizes.Small,
            variant: 'outlined',
            className: 'master-data-download master-data-download-secondary',
            iconOnly: true,
            'aria-label': 'Download Locations as Excel',
            title: 'Download Locations as Excel',
          }}
        />
        <Button
          size='small'
          iconOnly
          icon={<ArrowCcw />}
          onClick={getData}
          disabled={loading}
          data-testid='btn-refresh'
        />
      </div>
      <Card className='table-card'>
        <Table
          data={renderData(searchText, locations)}
          columns={columns}
          totalRecords={renderData(searchText, locations).length}
          recordsPerPage={NUMBER_20}
          isLoading={loading}
        />
      </Card>
      {openParams && (
        <Parameters
          open={openParams}
          onClose={handleParamsOpenClose}
          params={{
            name: location?.locationName ?? '',
            parameters: location?.parameters ?? [],
          }}
          paramType='Location'
        />
      )}
      <AddEditLocation
        open={open}
        onClose={handleModalOpenClose}
        location={location}
        refreshData={getData}
      />
    </NGContainer>
  );
};

export default Locations;
